import { Menu, MenuButton, MenuItem } from "@aws-amplify/ui-react";
import { FieldInstance, User, UserForResolver } from "db/definitions";
import { useTheme } from "hooks/useSingleton";
import React, { Dispatch, useEffect, useState, SetStateAction } from "react";
import { FiChevronDown } from "react-icons/fi";
import { GoSortAsc, GoSortDesc } from "react-icons/go";
import { TiMinus } from "react-icons/ti";
import getTableFields from "utils/getTableFields";
import { multiLevelSorting } from "utils/sort";
import updateTable from "utils/updateTable";
import { ThemeProvider, Theme } from "@aws-amplify/ui-react";
import { Option } from "db/definitions";

interface SortOptionsProps {
	columnIndex: number;
	userDoc: UserForResolver | User;
	tableInstance: FieldInstance;
	setTableInstance: any;
	setRowsData: any;
	setRowsHtml: any;
	online: boolean;
	tableFieldTemplateId: string;
	buildRows: any;
	setLoaded: Dispatch<SetStateAction<boolean>>;
}

export interface sortingPriorityObj {
	id: string;
	identifier: string;
	descending: boolean;
	date: boolean;
	createdBy: string;
	rank: number;
}

export const SortOptions = ({
	columnIndex,
	userDoc,
	tableInstance,
	setTableInstance,
	setRowsData,
	setRowsHtml,
	online,
	tableFieldTemplateId,
	buildRows,
	setLoaded,
}: SortOptionsProps) => {
	const color = useTheme();
	const [sortingPriority, setSortingPriority] = useState<{ [key: string]: sortingPriorityObj }>({});
	const [sortingTrigger, setSortingTrigger] = useState<boolean>(false);

	useEffect(() => {
		if (tableInstance.sortingPriority) setSortingPriority(JSON.parse(tableInstance.sortingPriority));
	}, [sortingTrigger]);

	const sortByColumn = (sp: { [key: string]: sortingPriorityObj }, rowArray: any, htmlArray: any) => {
		const newData = [];
		const newHtml = [];
		const newFieldsOrder = [];

		const sortHashTable: { [key: string]: FieldInstance[] | React.JSX.Element[] } = {};
		const sortedData = [];

		for (let i = 0; i < rowArray.length; i++) {
			const rowObject: { [key: string]: string } = {};
			let fieldsOrder = "";
			const numberOfColumns = rowArray[i].length;
			for (let j = 0; j < numberOfColumns; j++) {
				const field = rowArray[i][j];
				if (j === 0) {
					sortHashTable[`HTML-${field.id}`] = htmlArray[i];
					sortHashTable[`DATA-${field.id}`] = rowArray[i];
					rowObject["id"] = field.id;
				}
				if (field.fieldTemplate.inputType === "radio") {
					const option: Option | undefined = field.fieldTemplate.options.items.find((option: Option) => option.id === field.value);
					rowObject[field.fieldTemplate.header] = option ? option.label : "";
				} else if (["checkbox", "dropdown"].includes(field.fieldTemplate.inputType)) {
					const fieldIds = field.value.split("\u001f");
					const options: Option[] = field.fieldTemplate.options.items.filter((option: Option) => fieldIds.includes(option.id));
					rowObject[field.fieldTemplate.header] = "";
					fieldIds.forEach((fieldId: string, idx: number) => {
						const option = options.find((option: Option) => option.id === fieldId);
						if (option) rowObject[field.fieldTemplate.header] += idx !== fieldIds.length - 1 ? `${option.label}, ` : option.label;
					});
				} else {
					rowObject[field.fieldTemplate.header] = field.value;
				}
				fieldsOrder += `${field.id}${j === numberOfColumns - 1 ? "" : ","}`;
			}
			// Use the original fieldsOrder string to preserve hidden cell IDs
			rowObject["fieldsOrder"] = tableInstance.fieldsOrder![i];
			sortedData.push(rowObject);
		}
		if (Object.values(sp).length) multiLevelSorting({ array: sortedData, sortingPriority: Object.values(sp) });

		for (let i = 0; i < sortedData.length; i++) {
			const id = sortedData[i].id;
			const fieldsOrder = sortedData[i].fieldsOrder;
			if (sortHashTable[`DATA-${id}`]) {
				newData.push(sortHashTable[`DATA-${id}`]);
				newHtml.push(sortHashTable[`HTML-${id}`]);
			}
			newFieldsOrder.push(fieldsOrder);
		}

		const newTableInstance = { ...tableInstance };
		newTableInstance.fieldsOrder = newFieldsOrder;
		newTableInstance.sortingPriority = JSON.stringify(sp);
		newTableInstance.updatedAt = new Date().toISOString();
		setTableInstance(newTableInstance);
		setRowsData(newData);
		setRowsHtml(newHtml);
		return newTableInstance;
	};

	const handleSortByColumn = async (index: number, sortOption: string) => {
		if (tableInstance.fieldsOrder?.length === 0) return;
		setLoaded(false);
		const tableFieldsData = await getTableFields(tableInstance.id, online);
		const refreshedFieldInstance = { ...tableInstance, tableFields: { items: tableFieldsData } };
		const rows = buildRows(refreshedFieldInstance, true);
		const rowsArray = rows.rowsArray;
		const htmlArray = rows.rowsHtml;
		let newSortingPriority = sortingPriority;
		if (sortOption === "none" && newSortingPriority[tableFieldTemplateId]) {
			delete newSortingPriority[tableFieldTemplateId];
			const columns = Object.values(newSortingPriority);
			if (columns.length) {
				for (let i = 0; i < columns.length; i++) {
					columns[i].rank = i + 1;
				}
			}
		} else if (sortOption === "none") {
			setSortingPriority(newSortingPriority);
			const sorted = sortByColumn(newSortingPriority, rowsArray, htmlArray);
			await updateTable(sorted, online);
			setLoaded(true);
			return;
		} else {
			// Always recalculate identifier from the live data to fix stale/bad
			// identifiers that may have been persisted by a pre-fix sort.
			const matchedCell = rowsArray[0].find((cell: FieldInstance) => cell.fieldTemplateId === tableFieldTemplateId);
			const freshIdentifier = matchedCell?.fieldTemplate?.header ?? "";
			if (newSortingPriority[tableFieldTemplateId]) {
				newSortingPriority = {
					...newSortingPriority,
					[tableFieldTemplateId]: {
						...sortingPriority[tableFieldTemplateId],
						identifier: freshIdentifier,
						descending: sortOption === "descending" ? true : false,
					},
				};
			} else {
				newSortingPriority = {
					...newSortingPriority,
					[tableFieldTemplateId]: {
						id: tableFieldTemplateId,
						identifier: freshIdentifier,
						descending: sortOption === "descending" ? true : false,
						date: false,
						createdBy: userDoc.id,
						rank: Object.keys(newSortingPriority).length + 1,
					},
				};
			}
		}
		setSortingPriority(newSortingPriority);
		const sorted = sortByColumn(newSortingPriority, rowsArray, htmlArray);
		updateTable(sorted, online);
		setLoaded(true);
		setSortingTrigger(!sortingTrigger);
	};

	const theme: Theme = {
		name: "sort-theme",
		tokens: {
			components: {
				button: {
					paddingBlockStart: { value: "13px" },
					paddingBlockEnd: { value: "13px" },
					backgroundColor: { value: color.background.primary as string },
					_active: {
						backgroundColor: { value: color.background.primary as string },
						borderColor: { value: color.border.secondary as string },
					},
					_focus: {
						backgroundColor: { value: color.background.primary as string },
						borderColor: { value: color.border.secondary as string },
					},
				},
				menu: {
					backgroundColor: { value: color.background.secondary as string },
					flexDirection: { value: "column" },
					minWidth: { value: "100%" },
					item: {
						minHeight: { value: "10px" },
					},
				},
			},
		},
	};

	return (
		<ThemeProvider theme={theme} colorMode="light">
			<Menu
				trigger={
					<MenuButton
						data-test={`sort-column-trigger-${tableFieldTemplateId}`}
						style={{ background: color.background.primary, borderStyle: "none", color: color.text.primary }}>
						{sortingPriority[tableFieldTemplateId] ? (
							sortingPriority[tableFieldTemplateId].descending ? (
								<div style={{ display: "flex" }}>
									<GoSortDesc />
									<div>{sortingPriority[tableFieldTemplateId].rank}</div>
								</div>
							) : (
								<div style={{ display: "flex" }}>
									<GoSortAsc />
									<div>{sortingPriority[tableFieldTemplateId].rank}</div>
								</div>
							)
						) : (
							<FiChevronDown />
						)}
					</MenuButton>
				}
				menuAlign="center">
				<MenuItem
					data-test={`sort-none-${tableFieldTemplateId}`}
					style={{ color: color.text.tertiary, height: "40px" }}
					onClick={(e) => handleSortByColumn(columnIndex, "none")}>
					<TiMinus style={{ paddingRight: "8px" }} color={color.text.tertiary} />
					None
				</MenuItem>
				<MenuItem
					data-test={`sort-ascending-${tableFieldTemplateId}`}
					style={{ color: color.text.tertiary, height: "40px" }}
					onClick={(e) => handleSortByColumn(columnIndex, "ascending")}>
					<GoSortAsc style={{ paddingRight: "8px" }} color={color.text.tertiary} />
					Ascending
				</MenuItem>
				<MenuItem
					data-test={`sort-descending-${tableFieldTemplateId}`}
					style={{ color: color.text.tertiary, height: "40px" }}
					onClick={(e) => handleSortByColumn(columnIndex, "descending")}>
					<GoSortDesc style={{ paddingRight: "8px", color: color.text.tertiary }} />
					Descending
				</MenuItem>
			</Menu>
		</ThemeProvider>
	);
};
